import React, { useState, useRef, useEffect, useCallback } from 'react';
import { sendChatMessage } from '../api/client';
import { analytics } from '../analytics';

function renderMarkdown(text) {
  if (!text) return null;
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const html = escaped
    // Headers
    .replace(/^### (.+)$/gm, '<h4 class="chat-md-h">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="chat-md-h">$1</h3>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="chat-md-code">$1</code>')
    // Unordered lists (- items)
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    // Wrap consecutive <li> in <ul>
    .replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul class="chat-md-list">$1</ul>')
    // Numbered lists
    .replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>')
    // Paragraphs (double newline)
    .replace(/\n\n/g, '</p><p>')
    // Single newlines within paragraphs
    .replace(/\n/g, '<br/>');

  return <div className="chat-md" dangerouslySetInnerHTML={{ __html: `<p>${html}</p>` }} />;
}

export default function ChatBubble({ accessToken, schoolType }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const sendMessage = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    const userMessage = { role: 'user', content: text };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputValue('');
    setIsLoading(true);
    analytics.track('ai_assistant_message_sent', { question: text });

    try {
      const data = await sendChatMessage(accessToken, updatedMessages, schoolType || 'school');
      setMessages((prev) => [...prev, { role: 'assistant', content: data.response || 'I didn\'t get a response. Please try again.' }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', content: err?.message || 'Unable to reach the assistant. Please check your connection.' }]);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, messages, accessToken, schoolType]);

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function handleClear() {
    setMessages([]);
  }

  // Chat bubble icon (speech bubble SVG)
  const chatIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );

  // Close icon
  const closeIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  // Send icon
  const sendIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );

  if (!isOpen) {
    return (
      <button
        type="button"
        className="chat-bubble"
        onClick={() => setIsOpen(true)}
        aria-label="Open AI assistant"
      >
        {chatIcon}
      </button>
    );
  }

  return (
    <div className="chat-panel">
      {/* Header */}
      <div className="chat-header">
        <div className="chat-header-info">
          <strong>SmartSort Assistant</strong>
          <span>AI-powered help</span>
        </div>
        <div className="chat-header-actions">
          {messages.length > 0 ? (
            <button type="button" className="chat-clear-btn" onClick={handleClear} title="Clear chat">
              Clear
            </button>
          ) : null}
          <button type="button" className="chat-close-btn" onClick={() => setIsOpen(false)} aria-label="Close">
            {closeIcon}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <div className="chat-empty-icon">💬</div>
            <p><strong>Hi! I'm your SmartSort assistant.</strong></p>
            <p>Ask me anything about how to use SmartSort — importing students, running the solver, managing assignments, and more.</p>
          </div>
        ) : null}

        {messages.map((msg, i) => (
          <div key={i} className={`chat-msg chat-msg-${msg.role}`}>
            <div className="chat-msg-bubble">
              {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
            </div>
          </div>
        ))}

        {isLoading ? (
          <div className="chat-msg chat-msg-assistant">
            <div className="chat-msg-bubble chat-typing">
              <span /><span /><span />
            </div>
          </div>
        ) : null}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input-area">
        <input
          ref={inputRef}
          type="text"
          className="chat-input"
          placeholder="Ask a question..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <button
          type="button"
          className="chat-send-btn"
          onClick={sendMessage}
          disabled={isLoading || !inputValue.trim()}
          aria-label="Send message"
        >
          {sendIcon}
        </button>
      </div>
    </div>
  );
}
